import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useContainerResize } from '@/hooks';
import { TimeDomain, type TimelineSeekPreview } from '@/types/video-player';
import { Range, Root, Thumb, Track } from '@radix-ui/react-slider';
import { getVideoElementFromRef } from '@/utils/video-player';
import { formatVideoTime } from '@/utils/time';
import { computeGaps } from '@/utils/camera-stream';
import { MILLISECONDS_TO_SECONDS } from '@/constants/time';
import { TrackHighlight } from './TrackHighlight';
import { TrackGap } from './TrackGap';
import { VideoProgressBarThumbnail } from './VideoProgressBarThumbnail';
import { VideoProgressBarTimestamp } from './VideoProgressBarTimestamp';
import { CameraProgressBarThumbnail } from './CameraProgressBarThumbnail';
import { useVideoPlayerContext } from './VideoPlayerProvider';

interface VideoProgressBarProps {
    overlay?: boolean;
    variant?: 'default' | 'pip';
    visible: boolean;
}

export const VideoProgressBar: React.FC<VideoProgressBarProps> = ({
    overlay = true,
    variant = 'default',
    visible,
}) => {
    const ctx = useVideoPlayerContext();

    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isHoveringProgress, setIsHoveringProgress] = useState(false);
    const [hoveredTime, setHoveredTime] = useState(0);
    const [mouseX, setMouseX] = useState(0);
    const sliderContainerRef = useRef<HTMLDivElement>(null);
    const progressBarWidth = useContainerResize(sliderContainerRef);

    const {
        activeMediaRef,
        streaming,
        displayTimeline,
        playbackStartTimestamp,
        isCamera,
        sourceId,
        highlights,
        isTimelineMode,
        loopTimeRange,
        loop,
        setLoop,
        skipTooltipTime,
        emitter,
    } = ctx;

    const [seekPreview, setSeekPreview] = useState<TimelineSeekPreview | null>(
        null,
    );
    const windowKey = `${sourceId}:${highlights[0]?.id}:${loopTimeRange?.startTime}`;
    const [previousWindowKey, setPreviousWindowKey] = useState(windowKey);
    if (previousWindowKey !== windowKey) {
        setPreviousWindowKey(windowKey);
        setSeekPreview(null);
    }
    const windowBegin = displayTimeline?.earliest ?? undefined;
    const windowEnd = displayTimeline?.latest ?? undefined;
    const earliestTimestamp = seekPreview?.window.begin ?? windowBegin;
    const currentTimestamp = Math.floor(Date.now() / MILLISECONDS_TO_SECONDS);
    const streamBeginTimestamp = streaming.streamBeginTimestamp ?? undefined;
    const latestTimestamp =
        seekPreview?.window.end ??
        (isCamera && windowEnd != null
            ? streaming.live && loopTimeRange?.domain !== TimeDomain.ABSOLUTE
                ? currentTimestamp
                : Math.min(currentTimestamp, windowEnd)
            : windowEnd);

    useEffect(() => {
        // Radix keyboard commits can arrive before its preview value update.
        if (seekPreview && seekPreview.timestamp === playbackStartTimestamp) {
            setSeekPreview(null);
        }
    }, [seekPreview, playbackStartTimestamp]);

    const gaps = computeGaps(
        streaming.timeline?.intervals ?? [],
        Date.now() / MILLISECONDS_TO_SECONDS,
    );

    useEffect(() => {
        const syncCurrentTime = () => {
            const videoElement = getVideoElementFromRef(activeMediaRef);
            if (videoElement) setCurrentTime(videoElement.currentTime);
        };
        const syncDuration = () => {
            const videoElement = getVideoElementFromRef(activeMediaRef);
            if (!videoElement) return;
            const duration = videoElement.duration;
            if (!isNaN(duration) && isFinite(duration)) {
                setDuration(duration);
            }
        };

        const unsubscribeTimeUpdate = emitter.on('timeupdate', syncCurrentTime);
        const unsubscribeSeeked = emitter.on('seeked', syncCurrentTime);
        const unsubscribeDurationChange = emitter.on(
            'durationchange',
            syncDuration,
        );
        const unsubscribeLoadedMetadata = emitter.on('loadedmetadata', () => {
            syncDuration();
            syncCurrentTime();
        });

        const videoElement = getVideoElementFromRef(activeMediaRef);
        if (videoElement) {
            if (
                videoElement.duration &&
                !isNaN(videoElement.duration) &&
                isFinite(videoElement.duration)
            ) {
                setDuration(videoElement.duration);
            }
            setCurrentTime(videoElement.currentTime);
        }

        return () => {
            unsubscribeTimeUpdate();
            unsubscribeSeeked();
            unsubscribeDurationChange();
            unsubscribeLoadedMetadata();
        };
    }, [emitter, activeMediaRef]);

    const displayDuration =
        isTimelineMode && earliestTimestamp != null && latestTimestamp != null
            ? latestTimestamp - earliestTimestamp
            : duration;

    const isSeekable = displayDuration > 0;

    const timelineTimestamp =
        streaming.pendingSeekTimestamp ??
        (streamBeginTimestamp != null
            ? streamBeginTimestamp + currentTime
            : (playbackStartTimestamp ?? earliestTimestamp ?? 0));
    const timelineProgress = Math.max(
        0,
        Math.min(displayDuration, timelineTimestamp - (earliestTimestamp ?? 0)),
    );

    const displayProgress = seekPreview
        ? seekPreview.timestamp - (earliestTimestamp ?? 0)
        : streaming.live
          ? displayDuration
          : isTimelineMode && earliestTimestamp != null
            ? timelineProgress
            : currentTime;
    const progressPercent =
        displayDuration > 0
            ? Math.max(
                  0,
                  Math.min(100, (displayProgress / displayDuration) * 100),
              )
            : 0;

    const handleSeek = (value: number[]) => {
        if (loop && loopTimeRange) {
            const { startTime, endTime, domain } = loopTimeRange;
            const isMediaDomain = domain === TimeDomain.MEDIA;
            const seekedPosition = isMediaDomain
                ? value[0]
                : (earliestTimestamp ?? 0) + value[0];
            if (seekedPosition < startTime || seekedPosition > endTime) {
                setLoop(false);
            }
        }

        if (
            isTimelineMode &&
            streaming.onSeekToTimestamp &&
            earliestTimestamp != null
        ) {
            const absoluteTimestamp = earliestTimestamp + value[0];
            streaming.onSeekToTimestamp(absoluteTimestamp);
        } else {
            const newTime = value[0];
            const videoElement =
                activeMediaRef.current as HTMLVideoElement | null;
            if (videoElement) {
                videoElement.currentTime = newTime;
            }
            streaming.onSeek?.(newTime);
        }
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!sliderContainerRef.current || displayDuration === 0) return;

        const rect = sliderContainerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percentage = Math.max(0, Math.min(1, x / rect.width));
        const time = percentage * displayDuration;

        setMouseX(x);
        setHoveredTime(time);
    };

    const skipTooltipOffset =
        skipTooltipTime != null && isTimelineMode
            ? skipTooltipTime - (earliestTimestamp ?? 0)
            : skipTooltipTime;

    const skipTooltipX = useMemo(() => {
        if (
            skipTooltipOffset == null ||
            displayDuration === 0 ||
            progressBarWidth === 0
        ) {
            return null;
        }
        const percentage = skipTooltipOffset / displayDuration;
        return Math.max(
            0,
            Math.min(progressBarWidth, percentage * progressBarWidth),
        );
    }, [skipTooltipOffset, displayDuration, progressBarWidth]);

    const isTimelineTooltipActive =
        visible &&
        displayDuration > 0 &&
        isTimelineMode &&
        earliestTimestamp != null &&
        (isHoveringProgress || skipTooltipTime != null);

    const showThumbnailTooltip =
        visible &&
        displayDuration > 0 &&
        !isTimelineMode &&
        sourceId &&
        (isHoveringProgress || skipTooltipTime != null);
    const showCameraThumbnail =
        isTimelineTooltipActive && streaming.storageService != null && sourceId;
    const showTimestampFallback =
        isTimelineTooltipActive && !showCameraThumbnail;

    const tooltipTime = isHoveringProgress
        ? hoveredTime
        : (skipTooltipOffset ?? 0);
    const tooltipX = isHoveringProgress ? mouseX : (skipTooltipX ?? 0);

    const isPipVariant = variant === 'pip';

    const wrapperClasses = isPipVariant
        ? `relative transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`
        : overlay
          ? `absolute bottom-0 left-0 right-0 px-4 pb-3 pt-6 bg-gradient-to-t from-black/80 to-transparent transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`
          : `relative transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`;

    return (
        <div
            className={wrapperClasses}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
        >
            <div
                className={
                    isTimelineMode
                        ? 'w-full'
                        : isPipVariant
                          ? 'flex h-[18px] items-center gap-3'
                          : 'flex items-center gap-2'
                }
            >
                {!isTimelineMode && (
                    <span
                        className={
                            isPipVariant
                                ? 'flex h-[18px] shrink-0 items-center whitespace-nowrap tabular-nums text-xs font-normal leading-[18px] text-white font-geist'
                                : 'text-white text-sm font-normal font-geist min-w-[60px]'
                        }
                    >
                        {formatVideoTime(currentTime)}
                    </span>
                )}
                <div
                    ref={sliderContainerRef}
                    className={
                        isTimelineMode ? 'relative w-full' : 'relative flex-1'
                    }
                    onMouseMove={handleMouseMove}
                    onMouseEnter={() => setIsHoveringProgress(true)}
                    onMouseLeave={() => setIsHoveringProgress(false)}
                >
                    <Root
                        className={
                            isPipVariant
                                ? 'relative flex items-center select-none touch-none w-full h-[18px]'
                                : 'relative flex items-center select-none touch-none w-full h-6'
                        }
                        max={displayDuration}
                        onPointerCancel={() => setSeekPreview(null)}
                        onValueChange={(value) => {
                            if (
                                isTimelineMode &&
                                earliestTimestamp != null &&
                                latestTimestamp != null
                            ) {
                                setSeekPreview({
                                    timestamp: earliestTimestamp + value[0],
                                    window: {
                                        begin: earliestTimestamp,
                                        end: latestTimestamp,
                                    },
                                });
                            } else {
                                handleSeek(value);
                            }
                        }}
                        onValueCommit={(value) => {
                            if (isTimelineMode) {
                                handleSeek(value);
                                setSeekPreview(null);
                            }
                        }}
                        step={0.1}
                        value={[displayProgress]}
                        disabled={!isSeekable}
                    >
                        <Track
                            className={
                                isPipVariant
                                    ? 'bg-white/20 relative grow rounded-full h-[2px]'
                                    : 'bg-white/30 relative grow rounded-full h-[2px]'
                            }
                        >
                            <Range className="absolute bg-white rounded-full h-full transition-[width] duration-200 ease-out" />
                        </Track>
                        {gaps?.map((gap, idx) => (
                            <div
                                key={`gap-${idx}`}
                                className="absolute top-0 left-0 w-full h-full pointer-events-none"
                            >
                                <TrackGap
                                    gapStart={
                                        gap.begin - (earliestTimestamp ?? 0)
                                    }
                                    gapEnd={gap.end - (earliestTimestamp ?? 0)}
                                    totalDuration={displayDuration}
                                />
                            </div>
                        ))}
                        {highlights.map((highlight) => (
                            <div
                                key={highlight.id}
                                className="absolute top-0 left-0 w-full h-full pointer-events-none"
                            >
                                <TrackHighlight
                                    {...highlight}
                                    videoDuration={displayDuration}
                                    domain={highlight.domain}
                                    earliestTimestamp={earliestTimestamp}
                                />
                            </div>
                        ))}
                        <Thumb className="opacity-0" />
                    </Root>
                    {isTimelineMode && (
                        <div
                            className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-white pointer-events-none transition-[left] duration-200 ease-out"
                            style={{ left: `${progressPercent}%` }}
                        />
                    )}
                    {showThumbnailTooltip && (
                        <VideoProgressBarThumbnail
                            hoveredTime={tooltipTime}
                            mouseX={tooltipX}
                            progressBarWidth={progressBarWidth}
                            sourceId={sourceId!}
                        />
                    )}
                    {showCameraThumbnail && (
                        <CameraProgressBarThumbnail
                            hoveredTime={tooltipTime}
                            mouseX={tooltipX}
                            progressBarWidth={progressBarWidth}
                            earliestTimestamp={earliestTimestamp!}
                            storageService={streaming.storageService!}
                            sourceId={sourceId!}
                            timeline={streaming.timeline}
                        />
                    )}
                    {showTimestampFallback && (
                        <VideoProgressBarTimestamp
                            hoveredTime={tooltipTime}
                            mouseX={tooltipX}
                            progressBarWidth={progressBarWidth}
                            earliestTimestamp={earliestTimestamp!}
                        />
                    )}
                </div>
                {!isTimelineMode && (
                    <span
                        className={
                            isPipVariant
                                ? 'flex h-[18px] shrink-0 items-center justify-end whitespace-nowrap tabular-nums text-xs font-normal leading-[18px] text-white font-geist'
                                : 'text-white text-sm font-normal font-geist min-w-[60px] text-right'
                        }
                    >
                        {formatVideoTime(displayDuration)}
                    </span>
                )}
            </div>
        </div>
    );
};
